import { Badge, Button, cn } from "@devhop/ui";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  XCircle,
} from "lucide-react";
import { useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { KPIPeriodFilter, KPIStatus } from "../../domain/types";
import { useKPIOverviewExplainQuery } from "../api/queries";
import {
  searchToExplainArgs,
  searchToPeriodFilter,
} from "../utils/kpi-overview-explain-search";

const SCOPE_TITLES: Record<string, string> = {
  organization: "ລາຍລະອຽດຜົນ — ລະດັບອົງກອນ",
  department: "ລາຍລະອຽດຜົນ — ຕາມພະແນກ",
  position: "ລາຍລະອຽດຜົນ — ຕາມຕຳແໜ່ງ",
  employee: "ລາຍລະອຽດຜົນ — ລະດັບພະນັກງານ",
};

function periodLabel(period: KPIPeriodFilter): string {
  if (period.periodType === "monthly" && period.month) {
    const monthNames = [
      "ມັງກອນ",
      "ກຸມພາ",
      "ມີນາ",
      "ເມສາ",
      "ພຶດສະພາ",
      "ມິຖຸນາ",
      "ກໍລະກົດ",
      "ສິງຫາ",
      "ກັນຍາ",
      "ຕຸລາ",
      "ພະຈິກ",
      "ທັນວາ",
    ];
    return `${monthNames[period.month - 1] ?? ""} ${period.year}`;
  }
  if (period.periodType === "quarterly" && period.quarter) {
    return `ໄຕມາດ ${period.quarter} / ${period.year}`;
  }
  return String(period.year);
}

function statusStyle(status: KPIStatus): {
  label: string;
  className: string;
  Icon: typeof CheckCircle2;
} {
  switch (status) {
    case "pass":
      return {
        label: "ຜ່ານ",
        className:
          "border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950/30 dark:text-green-400",
        Icon: CheckCircle2,
      };
    case "warning":
      return {
        label: "ເກືອບຜ່ານ",
        className:
          "border-yellow-200 bg-yellow-50 text-yellow-900 dark:border-yellow-800 dark:bg-yellow-950/30 dark:text-yellow-300",
        Icon: AlertCircle,
      };
    case "fail":
      return {
        label: "ຕໍ່າກວ່າເປົ້າ",
        className:
          "border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-400",
        Icon: XCircle,
      };
    default:
      return {
        label: "ລໍຖ້າ",
        className: "border-border bg-muted text-muted-foreground",
        Icon: AlertCircle,
      };
  }
}

export function KPIOverviewExplainPage() {
  const nav = useNavigate();
  const rawSearch = useSearch({ from: "/app/management/okr-kpi/explain" });

  const filter = useMemo(() => searchToPeriodFilter(rawSearch), [rawSearch]);
  const explainArgs = useMemo(
    () => searchToExplainArgs(rawSearch),
    [rawSearch],
  );

  const queryEnabled =
    (explainArgs.scope !== "department" ||
      Boolean(explainArgs.scopeDepartmentId?.trim())) &&
    (explainArgs.scope !== "position" ||
      Boolean(explainArgs.scopePositionId?.trim()));

  const q = useKPIOverviewExplainQuery(filter, explainArgs, queryEnabled);

  const data = q.data;
  const scope = data?.scope ?? explainArgs.scope;
  const title = scope
    ? (SCOPE_TITLES[scope] ?? "ລາຍລະອຽດຜົນ KPI")
    : "ລາຍລະອຽດຜົນ KPI";

  return (
    <>
      <Header />
      <Main>
        <div className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="font-bold text-2xl tracking-tight">{title}</h1>
              <p className="mt-1 text-muted-foreground text-sm">
                {periodLabel(filter)}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => nav({ to: "/app/management/okr-kpi" })}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              ກັບພາບລວມ KPI
            </Button>
          </div>

          {!queryEnabled && (
            <p className="text-destructive text-sm">
              ລິ້ງບໍ່ຄົບ: ຕ້ອງລະບຸແຜນກ (scopeDepartmentId) ເມື່ອເລືອກລະດັບພະແນກ
              ຫຼື ຕຳແໜ່ງ (scopePositionId) ເມື່ອເລືອກລະດັບຕຳແໜ່ງ
            </p>
          )}

          {queryEnabled && q.isLoading && (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              ກຳລັງໂຫຼດ...
            </div>
          )}
          {queryEnabled && q.isError && (
            <p className="text-destructive text-sm">
              ບໍ່ສາມາດໂຫຼດລາຍລະອຽດໄດ້ — ອາດບໍ່ມີຂໍ້ມູນໃນງວດ ຫຼື ລິ້ງບໍ່ຖືກຕ້ອງ
            </p>
          )}

          {data && (
            <div className="space-y-6">
              {data.scope === "department" && data.scopeDepartmentName && (
                <p className="font-medium text-sm">
                  ພະແນກ: {data.scopeDepartmentName}
                </p>
              )}
              {data.scope === "position" && data.scopePositionName && (
                <p className="font-medium text-sm">
                  ຕຳແໜ່ງ: {data.scopePositionName}
                </p>
              )}

              <div className="grid grid-cols-2 gap-2 gap-y-3 sm:grid-cols-4">
                <div className="rounded-lg border bg-card p-3">
                  <p className="text-muted-foreground text-xs">ທັງໝົດ</p>
                  <p className="font-semibold text-lg">{data.summary.total}</p>
                </div>
                <div className="rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-900 dark:bg-green-950/20">
                  <p className="text-green-800 text-xs dark:text-green-400">
                    ຜ່ານ
                  </p>
                  <p className="font-semibold text-green-800 text-lg dark:text-green-400">
                    {data.summary.pass}
                  </p>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/20">
                  <p className="text-red-800 text-xs dark:text-red-400">
                    ຕໍ່າກວ່າເປົ້າ
                  </p>
                  <p className="font-semibold text-lg text-red-800 dark:text-red-400">
                    {data.summary.fail}
                  </p>
                </div>
                <div className="rounded-lg border bg-card p-3">
                  <p className="text-muted-foreground text-xs">ອັດຕາຜ່ານ</p>
                  <p className="font-semibold text-lg">
                    {data.summary.passRate.toFixed(1)}%
                  </p>
                </div>
              </div>

              {data.summary.averageLinkedPercent != null && (
                <div className="rounded-lg border bg-muted/40 p-3">
                  <p className="text-muted-foreground text-xs">
                    ຄ່າເຊື່ອມສະເລ່ຍ (ຕາມພາບລວມ)
                  </p>
                  <p className="font-semibold text-lg">
                    {data.summary.averageLinkedPercent.toFixed(1)}%
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <h2 className="font-medium text-base">ວິທີຄິດສະຖານະ</h2>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {data.formulaExplanation}
                </p>
              </div>

              <div className="space-y-3">
                <h2 className="font-medium text-base">
                  ລາຍການ KPI ໃນງວດນີ້
                  {explainArgs.kpiId ? " (ກອງຕາມລາຍການທີ່ເລືອກ)" : ""}
                </h2>
                {data.kpis.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    ບໍ່ມີລາຍການ KPI ທີ່ກົງກັບຕົວກອງ ໃນໄລຍະນີ້
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {data.kpis.map((k) => {
                      const st = statusStyle(k.status);
                      const Icon = st.Icon;
                      return (
                        <li
                          key={k.id}
                          className="rounded-lg border bg-card p-3 text-sm"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <p className="font-medium">{k.name}</p>
                              <p className="font-mono text-muted-foreground text-xs">
                                ຄ່າຈິງ {k.actualValue.toLocaleString()} / ເປົ້າ{" "}
                                {k.targetValue.toLocaleString()} · ອັດຕາ{" "}
                                {k.passRate.toFixed(1)}%
                              </p>
                            </div>
                            <Badge
                              variant="outline"
                              className={cn("shrink-0 gap-1", st.className)}
                            >
                              <Icon className="h-3.5 w-3.5" />
                              {st.label}
                            </Badge>
                          </div>
                          {k.measurementMethod && (
                            <p className="mt-2 text-muted-foreground">
                              <span className="font-medium text-foreground">
                                ວິທີວັດ:
                              </span>{" "}
                              {k.measurementMethod}
                            </p>
                          )}
                          {k.measurementSource && (
                            <p className="mt-1 text-muted-foreground">
                              <span className="font-medium text-foreground">
                                ແຫຼ່ງຂໍ້ມູນ:
                              </span>{" "}
                              {k.measurementSource}
                            </p>
                          )}
                          {(k.passCriteria || k.failCriteria) && (
                            <p className="mt-1 text-muted-foreground">
                              {k.passCriteria && (
                                <>
                                  <span className="font-medium text-foreground">
                                    ຜ່ານ:
                                  </span>{" "}
                                  {k.passCriteria}{" "}
                                </>
                              )}
                              {k.failCriteria && (
                                <>
                                  <span className="font-medium text-foreground">
                                    ບໍ່ຜ່ານ:
                                  </span>{" "}
                                  {k.failCriteria}
                                </>
                              )}
                            </p>
                          )}
                          {k.description && (
                            <p className="mt-2 text-muted-foreground text-xs">
                              {k.description}
                            </p>
                          )}
                          {k.sourceBreakdown &&
                            k.sourceBreakdown.length > 0 && (
                              <div className="mt-3 overflow-x-auto rounded-md border bg-muted/30 p-3">
                                <p className="mb-2 font-medium text-foreground text-xs">
                                  ຈຳນວນບັນທຶກຕໍ່ແຫຼ່ງ (ໃນງວດນີ້)
                                </p>
                                <table className="w-full min-w-[280px] text-left text-xs">
                                  <thead>
                                    <tr className="border-b text-muted-foreground">
                                      <th className="py-1 pr-2 font-medium">
                                        ແຫຼ່ງຂໍ້ມູນ
                                      </th>
                                      <th className="py-1 font-medium">
                                        ຈຳນວນ
                                      </th>
                                      <th className="py-1 font-medium">
                                        ຕົວຢ່າງ / ລິ້ງ
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {k.sourceBreakdown.map((row) => (
                                      <tr
                                        key={row.source}
                                        className="border-border/60 border-b last:border-0"
                                      >
                                        <td className="py-1.5 pr-2">
                                          {row.label}
                                        </td>
                                        <td className="py-1.5 tabular-nums">
                                          {row.recordCount.toLocaleString()}
                                        </td>
                                        <td className="max-w-[220px] py-1.5 align-top">
                                          {row.samples &&
                                          row.samples.length > 0 ? (
                                            <ul className="space-y-1">
                                              {row.samples.map((s) => (
                                                <li key={s.id}>
                                                  <Link
                                                    to={s.href}
                                                    className="text-primary underline underline-offset-2 hover:text-primary/90"
                                                  >
                                                    {s.label}
                                                  </Link>
                                                </li>
                                              ))}
                                            </ul>
                                          ) : (
                                            <span className="text-muted-foreground">
                                              —
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          <div className="mt-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                nav({
                                  to: "/app/kpi/$id",
                                  params: { id: k.id },
                                })
                              }
                            >
                              ເປີດນິຍາມ KPI
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </Main>
    </>
  );
}
